import React, { useRef, useState } from 'react';
import { usePrintHook } from '../../hooks/usePrintHook';
import { codyQ3Data, codyQ3MonthlyBreakdown } from '../../data/codyQ3Data';
import CCMarketingReport from './CCMarketingReport';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
  LineChart, Line, PieChart, Pie, Cell
} from 'recharts';
import { formatCurrency, formatPercentage } from '../../utils/formatters';

// Colors for charts
const COLORS = [
  '#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884D8',
  '#82CA9D', '#FFCC00', '#FF6666', '#5470C6', '#91CC75'
];

const StandaloneCCQ3Report: React.FC = () => {
  const reportRef = useRef<HTMLDivElement>(null);
  const { printRef } = usePrintHook();
  const [activeTab, setActiveTab] = useState<'overview' | 'monthly' | 'full'>('overview');

  // Generate PDF from report
  const generatePDF = async () => {
    if (!reportRef.current) return;

    try {
      // Create filename for the PDF
      const fileName = `ROI_Direct_Sales_Report_Q3_2025_Cody.pdf`;

      // Configure dimensions
      const reportElement = reportRef.current;
      const canvas = await html2canvas(reportElement, {
        scale: 1.5, // Higher scale for better quality
        useCORS: true, // Allow images from other domains
        logging: false,
      });

      const imgData = canvas.toDataURL('image/png');

      // Page setup (portrait A4)
      const pdf = new jsPDF({
        orientation: 'p',
        unit: 'mm',
        format: 'a4',
      });

      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = pdf.internal.pageSize.getHeight();

      // Calculate ratio to fit content to PDF
      const canvasRatio = canvas.height / canvas.width;
      const contentWidth = pdfWidth;
      const contentHeight = contentWidth * canvasRatio;

      // Add content spread across multiple pages if needed
      let heightLeft = contentHeight;
      let position = 0;
      let page = 1;

      pdf.addImage(imgData, 'PNG', 0, position, contentWidth, contentHeight);
      heightLeft -= pdfHeight;

      // Add more pages if content overflows
      while (heightLeft > 0) {
        position = -pdfHeight * page;
        pdf.addPage();
        pdf.addImage(imgData, 'PNG', 0, position, contentWidth, contentHeight);
        heightLeft -= pdfHeight;
        page++;
      }

      // Add footer on each page
      for (let i = 0; i < page; i++) {
        pdf.setPage(i + 1);
        pdf.setFontSize(8);
        pdf.setTextColor(100, 100, 100);
        pdf.text(`ROI Exteriors Direct Sales Report - Page ${i + 1} of ${page}`, 10, pdfHeight - 5);
      }

      // Download PDF
      pdf.save(fileName);
    } catch (error) {
      console.error('Error generating PDF:', error);
    }
  };

  // Handle export to PDF
  const handleExportPDF = () => {
    generatePDF();
  };

  // Handle print
  const handlePrint = () => {
    if (reportRef.current) {
      // Cast the ref to the expected type
      printRef(reportRef as unknown as React.RefObject<HTMLElement>);
    }
  };

  return (
    <div className="container mx-auto py-8 px-4">
      <div className="bg-white rounded-lg shadow-md p-6">
        <div className="mb-4">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-xl font-bold text-gray-800">
              Q3 2025 Direct Sales Report - Cody (Consolidated)
            </h2>
            <div className="flex space-x-3">
              <button
                onClick={handlePrint}
                className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded transition-colors"
              >
                Print
              </button>
              <button
                onClick={handleExportPDF}
                className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded transition-colors"
              >
                Export PDF
              </button>
            </div>
          </div>

        </div>

        {/* Tab Navigation */}
        <div className="border-b border-gray-200 mb-6">
          <nav className="-mb-px flex space-x-8">
            <button
              onClick={() => setActiveTab('overview')}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'overview'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Q3 Overview
            </button>
            <button
              onClick={() => setActiveTab('monthly')}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'monthly'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Monthly Breakdown
            </button>
            <button
              onClick={() => setActiveTab('full')}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'full'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Full Report
            </button>
          </nav>
        </div>

        <div ref={reportRef}>
          {activeTab === 'overview' && (
            <div className="space-y-6">
              {/* Q3 Summary Cards */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                <div className="bg-blue-50 p-4 rounded-lg">
                  <p className="text-sm text-gray-600">Total Sales</p>
                  <p className="text-2xl font-bold text-blue-600">
                    {formatCurrency(codyQ3Data.kpis.totalSales)}
                  </p>
                </div>
                <div className="bg-green-50 p-4 rounded-lg">
                  <p className="text-sm text-gray-600">Gross Profit</p>
                  <p className="text-2xl font-bold text-green-600">
                    {formatCurrency(codyQ3Data.kpis.grossProfit)}
                  </p>
                </div>
                <div className="bg-purple-50 p-4 rounded-lg">
                  <p className="text-sm text-gray-600">Net Profit</p>
                  <p className="text-2xl font-bold text-purple-600">
                    {formatCurrency(codyQ3Data.kpis.netProfit)}
                  </p>
                </div>
                <div className="bg-amber-50 p-4 rounded-lg">
                  <p className="text-sm text-gray-600">Total Jobs</p>
                  <p className="text-2xl font-bold text-amber-600">
                    {codyQ3Data.jobData.length}
                  </p>
                </div>
              </div>

              {/* Lead Source Distribution Pie Chart */}
              <div className="bg-gray-50 p-4 rounded-lg">
                <h3 className="text-lg font-semibold mb-4">Q3 Lead Source Distribution</h3>
                <ResponsiveContainer width="100%" height={300}>
                  <PieChart>
                    <Pie
                      data={codyQ3Data.salesBySource}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      label={(entry) => `${entry.source}: ${formatCurrency(entry.totalSales)}`}
                      outerRadius={80}
                      fill="#8884d8"
                      dataKey="totalSales"
                    >
                      {codyQ3Data.salesBySource.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip formatter={(value) => formatCurrency(value as number)} />
                  </PieChart>
                </ResponsiveContainer>
              </div>

              {/* Trade Performance Bar Chart */}
              <div className="bg-gray-50 p-4 rounded-lg">
                <h3 className="text-lg font-semibold mb-4">Q3 Performance by Trade</h3>
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={codyQ3Data.salesByTrade}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="trade" />
                    <YAxis />
                    <Tooltip formatter={(value) => formatCurrency(value as number)} />
                    <Legend />
                    <Bar dataKey="totalSales" fill="#0088FE" name="Total Sales" />
                    <Bar dataKey="grossProfit" fill="#00C49F" name="Gross Profit" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          )}

          {activeTab === 'monthly' && (
            <div className="space-y-6">
              {/* Monthly Trend Chart */}
              <div className="bg-gray-50 p-4 rounded-lg">
                <h3 className="text-lg font-semibold mb-4">Monthly Sales Trend</h3>
                <ResponsiveContainer width="100%" height={300}>
                  <LineChart data={codyQ3MonthlyBreakdown}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="month" />
                    <YAxis />
                    <Tooltip formatter={(value) => formatCurrency(value as number)} />
                    <Legend />
                    <Line type="monotone" dataKey="totalSales" stroke="#8884d8" name="Total Sales" />
                    <Line type="monotone" dataKey="grossProfit" stroke="#82ca9d" name="Gross Profit" />
                    <Line type="monotone" dataKey="netProfit" stroke="#ffc658" name="Net Profit" />
                  </LineChart>
                </ResponsiveContainer>
              </div>

              {/* Monthly Comparison Table */}
              <div className="bg-gray-50 p-4 rounded-lg">
                <h3 className="text-lg font-semibold mb-4">Monthly Comparison</h3>
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-100">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Month</th>
                        <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Jobs</th>
                        <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Total Sales</th>
                        <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Gross Profit</th>
                        <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Net Profit</th>
                        <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Net Margin</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {codyQ3MonthlyBreakdown.map((month, index) => (
                        <tr key={index}>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{month.month}</td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 text-right">{month.jobCount}</td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 text-right">{formatCurrency(month.totalSales)}</td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 text-right">{formatCurrency(month.grossProfit)}</td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 text-right">{formatCurrency(month.netProfit)}</td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 text-right">
                            {formatPercentage(month.totalSales > 0 ? month.netProfit / month.totalSales : 0)}
                          </td>
                        </tr>
                      ))}
                      <tr className="bg-gray-50 font-semibold">
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">Q3 Total</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-right">{codyQ3Data.jobData.length}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-right">{formatCurrency(codyQ3Data.kpis.totalSales)}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-right">{formatCurrency(codyQ3Data.kpis.grossProfit)}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-right">{formatCurrency(codyQ3Data.kpis.netProfit)}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-right">{formatPercentage(codyQ3Data.kpis.netProfitPercent)}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'full' && (
            <CCMarketingReport
              data={codyQ3Data}
              hideHeader={true}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default StandaloneCCQ3Report;
